/**
 * The five reports, as the page reads them.
 *
 * Types are hand-mirrored from `apps/api/src/modules/crm-reports/types.ts` - the
 * two databases and the two builds share no code, which is the same arrangement
 * every other lib module here lives with. Money is cents, time is MINUTES.
 */
import { api, API_BASE } from "./api";

export interface ReportMeta {
  from: string;
  to: string;
  months: string[];
}

export interface SalesReport {
  meta: ReportMeta;
  months: { month: string; invoicedCents: number; collectedCents: number }[];
  totals: {
    invoicedCents: number;
    collectedCents: number;
    outstandingCents: number;
    overdueCents: number;
    creditedCents: number;
    invoiceCount: number;
    creditCount: number;
    avgInvoiceCents: number;
  };
  byStatus: { status: string; count: number; totalCents: number }[];
  topClients: { clientId: string; name: string; collectedCents: number }[];
}

export interface ExpensesReport {
  meta: ReportMeta;
  months: { month: string; amountCents: number; taxCents: number }[];
  totals: { amountCents: number; taxCents: number; count: number; billableCents: number; billedCents: number };
  byCategory: { category: string; amountCents: number; count: number }[];
  byVendor: { vendor: string; amountCents: number; count: number }[];
}

export interface ProfitReport {
  meta: ReportMeta;
  months: { month: string; incomeCents: number; expenseCents: number; netCents: number }[];
  totals: {
    incomeCents: number;
    expenseCents: number;
    expenseTaxCents: number;
    netCents: number;
    marginPct: number | null;
  };
}

export interface LeadsReport {
  meta: ReportMeta;
  months: { month: string; created: number; converted: number }[];
  totals: {
    created: number;
    converted: number;
    open: number;
    won: number;
    lost: number;
    conversionPct: number | null;
    valueCents: number;
    wonValueCents: number;
  };
  byStatus: { status: string; count: number; valueCents: number }[];
  bySource: { source: string; count: number }[];
  byOwner: { userId: string; name: string; count: number; won: number }[];
}

export interface TimesheetsReport {
  meta: ReportMeta;
  months: { month: string; minutes: number; billableMinutes: number }[];
  totals: { minutes: number; billableMinutes: number; billedMinutes: number; entries: number };
  byUser: { userId: string; name: string; minutes: number; billableMinutes: number; billedMinutes: number }[];
  byProject: { projectId: string; name: string; minutes: number; billableMinutes: number }[];
}

/** The window, as both ends of a date picker leave it. Empty means "the server's
 *  default window", which is the last twelve months. */
export interface ReportRangeInput {
  from?: string;
  to?: string;
}

function qs(range: ReportRangeInput): string {
  const p = new URLSearchParams();
  if (range.from) p.set("from", range.from);
  if (range.to) p.set("to", range.to);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const getSalesReport = (range: ReportRangeInput, signal?: AbortSignal) =>
  api.get<SalesReport>(`/admin/crm/reports/sales${qs(range)}`, signal);

export const getExpensesReport = (range: ReportRangeInput, signal?: AbortSignal) =>
  api.get<ExpensesReport>(`/admin/crm/reports/expenses${qs(range)}`, signal);

export const getProfitReport = (range: ReportRangeInput, signal?: AbortSignal) =>
  api.get<ProfitReport>(`/admin/crm/reports/profit${qs(range)}`, signal);

export const getLeadsReport = (range: ReportRangeInput, signal?: AbortSignal) =>
  api.get<LeadsReport>(`/admin/crm/reports/leads${qs(range)}`, signal);

export const getTimesheetsReport = (range: ReportRangeInput, signal?: AbortSignal) =>
  api.get<TimesheetsReport>(`/admin/crm/reports/timesheets${qs(range)}`, signal);

/** Minutes as decimal hours, for a column that has to add up on screen. */
export function toHours(minutes: number): number {
  return Math.round((minutes / 60) * 10) / 10;
}

/**
 * The report kinds the export route answers to.
 *
 * "timesheets", not the page's own tab name "time": the tab labels a heading,
 * the route names a collector, and they have differed since the report shipped.
 * `REPORT_FOR_TAB` below is the one place that knows.
 */
export type ReportKind = "sales" | "expenses" | "profit" | "leads" | "timesheets";

/** The five tabs on the page, named as the page's own `?tab=` names them. */
export type ReportTab = "sales" | "expenses" | "profit" | "leads" | "time";

/**
 * The page's tab key to the route's report kind.
 *
 * Four of the five are the same word and the fifth is not, which is exactly the
 * shape that gets "fixed" by someone passing the tab straight through. A map
 * with all five spelled out makes the odd one out visible, and typing it over
 * `ReportTab` means a sixth tab cannot be added without landing here.
 */
export const REPORT_FOR_TAB: Record<ReportTab, ReportKind> = {
  sales: "sales",
  expenses: "expenses",
  profit: "profit",
  leads: "leads",
  time: "timesheets",
};

/**
 * One downloadable table.
 *
 * Hand-mirrored from `apps/api/src/modules/crm-reports/lib/csv-tables.ts`, the
 * same arrangement the report payload types above live with. The server
 * validates the pair, so a key that drifts out of this list is a 404 on a menu
 * item rather than a wrong file.
 *
 * `capped` is here rather than left to the server's response header because it
 * has to be readable BEFORE the press: three of these blocks are a top-N, and
 * an operator choosing "Top clients" should see that it is eight rows while
 * they are still deciding, not after opening the file.
 */
export interface ReportCsvTable {
  table: string;
  /** `reports.csv.<key>` in the catalogues. */
  labelKey: string;
  capped: number | null;
}

export const REPORT_CSV_TABLES: Record<ReportKind, ReportCsvTable[]> = {
  sales: [
    { table: "months", labelKey: "months", capped: null },
    { table: "status", labelKey: "salesStatus", capped: null },
    { table: "clients", labelKey: "salesClients", capped: 8 },
  ],
  expenses: [
    { table: "months", labelKey: "months", capped: null },
    { table: "category", labelKey: "expenseCategory", capped: null },
    { table: "vendors", labelKey: "expenseVendors", capped: 8 },
  ],
  profit: [{ table: "months", labelKey: "months", capped: null }],
  leads: [
    { table: "months", labelKey: "months", capped: null },
    { table: "status", labelKey: "leadStatus", capped: null },
    { table: "sources", labelKey: "leadSources", capped: null },
    { table: "owners", labelKey: "leadOwners", capped: 10 },
  ],
  timesheets: [
    { table: "months", labelKey: "months", capped: null },
    { table: "people", labelKey: "timePeople", capped: null },
    { table: "projects", labelKey: "timeProjects", capped: null },
  ],
};

/**
 * Ask for one table and hand the browser the file.
 *
 * `fetch` plus an object URL rather than pointing a link at the route, and the
 * reason is the failure: a plain link that 403s replaces the page with a JSON
 * error body, and the operator loses the range they had picked. This way a
 * refusal stays a refusal and the page is still there behind it.
 *
 * The filename is left to `Content-Disposition`. The server knows the window it
 * actually used, which is not always the window that was asked for.
 */
export async function downloadReportCsv(
  report: ReportKind,
  table: string,
  range: ReportRangeInput,
): Promise<void> {
  const p = new URLSearchParams({ table });
  if (range.from) p.set("from", range.from);
  if (range.to) p.set("to", range.to);

  const res = await fetch(`${API_BASE}/admin/crm/reports/${report}/export.csv?${p}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const name = filenameFrom(res.headers.get("Content-Disposition")) ?? `${report}-${table}.csv`;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Freed on the next tick, not immediately: Safari has not started the download
  // by the time click() returns, and revoking under it cancels it. The same note
  // `lib/csv.ts` and `lib/bulkPdf.ts` both carry.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** The quoted filename out of a `Content-Disposition`, or null. */
function filenameFrom(header: string | null): string | null {
  const m = header?.match(/filename="([^"]+)"/);
  return m?.[1] ?? null;
}
